import { ExternalLink } from "lucide-react";
import { Countdown } from "./Countdown";

const Stamp = ({ source }) =>
  source ? (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      className="mono mt-2 block max-w-full text-[0.55rem] uppercase leading-tight tracking-[0.14em] text-zinc-500 hover:text-ochre"
    >
      <span className="break-words">
        {source.publisher} <ExternalLink size={9} className="ml-0.5 inline align-baseline" />
      </span>
    </a>
  ) : null;

/** AFCON PAMOJA 2027 is the fixed point every build phase is timed against. */
export const AfconBand = ({ afcon }) =>
  afcon ? (
    <div className="mt-14 border border-maasai/40 bg-[#0b0b0b]" data-testid="afcon-band">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-maasai/30 p-6 md:p-8">
        <div>
          <p className="overline text-maasai">{afcon.name}</p>
          <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-none tracking-tight text-white md:text-4xl">
            {afcon.headline}
          </h3>
        </div>
        <p className="mono text-[0.6rem] uppercase tracking-[0.18em] text-ochre" data-testid="afcon-dates">
          Kick-off {afcon.kick_off} · Final {afcon.final} · {afcon.hosts}
        </p>
      </div>
      <p className="max-w-4xl px-6 pt-6 text-sm leading-relaxed text-zinc-400 md:px-8">{afcon.note}</p>
      <div className="grid gap-8 px-6 pt-7 lg:grid-cols-[minmax(0,340px)_1fr] md:px-8">
        <Countdown target={afcon.kick_off_iso} />
        {afcon.finished_by_kick_off ? (
          <div data-testid="afcon-checklist">
            <p className="overline !text-zinc-500">Finished by kick-off</p>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {afcon.finished_by_kick_off.map((item) => (
                <li key={item} className="flex gap-2.5 text-xs leading-relaxed text-zinc-300">
                  <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-ochre" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      <div className="grid gap-px bg-zinc-800 p-6 md:grid-cols-3 md:p-8">
        {afcon.phases.map((p) => (
          <div key={p.phase} className="bg-[#0b0b0b] p-6" data-testid={`afcon-phase-${p.phase.toLowerCase()}`}>
            <p className="font-display text-xl uppercase tracking-tight text-maasai md:text-2xl">{p.phase}</p>
            <p className="mono mt-2 text-[0.58rem] uppercase tracking-[0.16em] text-ochre">{p.window}</p>
            <p className="mt-4 text-sm leading-relaxed text-white">{p.aim}</p>
            <p className="mt-3 text-xs leading-relaxed text-zinc-500">{p.detail}</p>
          </div>
        ))}
      </div>
    </div>
  ) : null;

const KplAnchor = ({ plan }) => (
  <div className="mt-14 border border-white/10 bg-[#0b0b0b] p-6 md:p-8" data-testid="kpl-anchor">
    <p className="overline">Anchored to</p>
    <h3 className="display-3 mt-3">{plan.title}</h3>
    <p className="body-copy mt-4 max-w-3xl">{plan.standfirst}</p>
    <Stamp source={plan.source} />

    <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
      {plan.objectives.map((o) => (
        <div key={o.n} className="bg-[#0b0b0b] p-5" data-testid={`kpl-objective-${o.n}`}>
          <p className="mono text-[0.55rem] tracking-[0.2em] text-zinc-600">{o.n}</p>
          <p className="mt-2 font-display text-lg uppercase tracking-tight text-ochre">{o.title}</p>
          <p className="mt-3 text-xs leading-relaxed text-zinc-400">{o.detail}</p>
          <p className="mt-3 border-l-2 border-maasai pl-3 text-xs leading-relaxed text-white">{o.our_step}</p>
        </div>
      ))}
    </div>

    <div className="mt-8 grid gap-8 lg:grid-cols-2">
      <div className="border-l-2 border-ochre pl-5" data-testid="kpl-geography">
        <p className="overline text-ochre">The distribution problem</p>
        <p className="mono mt-3 text-2xl font-bold text-white">
          {plan.geography.clubs_in_nairobi_pct}% of clubs in Nairobi
        </p>
        <p className="mt-2 text-sm leading-relaxed text-zinc-400">
          {plan.geography.population_outside_nairobi_m}m people — {plan.geography.population_outside_nairobi_pct}% of
          Kenya — live outside it. {plan.geography.goal}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-white">{plan.geography.our_response}</p>
      </div>
      <div className="border-l-2 border-ochre pl-5" data-testid="kpl-licensing">
        <p className="overline text-ochre">FKF club licensing</p>
        <p className="mt-3 flex flex-wrap gap-2">
          {plan.licensing.pillars.map((p) => (
            <span key={p} className="mono border border-white/15 px-2 py-1 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-300">
              {p}
            </span>
          ))}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-zinc-400">{plan.licensing.youth_requirement}</p>
        <p className="mt-3 text-sm leading-relaxed text-white">{plan.licensing.our_response}</p>
      </div>
    </div>

    <div className="mt-8 overflow-x-auto">
      <div className="flex min-w-[760px] gap-px bg-zinc-800" data-testid="kpl-roadmap">
        {plan.roadmap.map(([year, note]) => (
          <div key={year} className="flex-1 bg-[#0b0b0b] p-4">
            <p className="mono text-[0.6rem] tracking-[0.18em] text-ochre">{year}</p>
            <p className="mt-2 text-xs leading-relaxed text-zinc-400">{note}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const GrassrootsAnchor = ({ anchor }) => (
  <div className="mt-14 space-y-8" data-testid="grassroots-anchor">
    <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8">
      <p className="overline">Anchored to</p>
      <h3 className="display-3 mt-3">{anchor.england.title}</h3>
      <p className="body-copy mt-4 max-w-3xl">{anchor.england.standfirst}</p>
      <div className="mt-7 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
        {anchor.england.rows.map((r, i) => (
          <div key={r.measure} className="bg-[#0b0b0b] p-5" data-testid={`england-row-${i}`}>
            <p className="mono text-xl font-bold text-ochre md:text-2xl">{r.value}</p>
            <p className="mt-2 text-[0.62rem] uppercase leading-relaxed tracking-[0.12em] text-white">{r.measure}</p>
            <p className="mt-2 text-xs leading-relaxed text-zinc-500">{r.note}</p>
            <Stamp source={r.source} />
          </div>
        ))}
      </div>
      <p className="mt-7 max-w-4xl border-l-2 border-maasai pl-5 text-sm leading-relaxed text-white">
        {anchor.england.lesson}
      </p>
    </div>

    <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8">
      <p className="overline text-ochre">The five-a-side case</p>
      <h3 className="display-3 mt-3">{anchor.brazil.title}</h3>
      <p className="body-copy mt-4 max-w-3xl">{anchor.brazil.standfirst}</p>
      <Stamp source={anchor.brazil.source} />
      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div>
          <p className="overline">How it happened</p>
          <div className="mt-4 space-y-4">
            {anchor.brazil.timeline.map(([era, note]) => (
              <div key={era} className="border-l border-white/15 pl-4" data-testid={`futsal-era-${era}`}>
                <p className="mono text-[0.58rem] uppercase tracking-[0.18em] text-ochre">{era}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">{note}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="overline">Why the court produces players</p>
          <ul className="mt-4 space-y-3">
            {anchor.brazil.why_it_works.map((line) => (
              <li key={line} className="flex gap-3 text-sm leading-relaxed text-zinc-300">
                <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-maasai" />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mt-8 max-w-4xl border-l-2 border-maasai pl-5 text-sm leading-relaxed text-white" data-testid="futsal-argument">
        {anchor.brazil.the_argument}
      </p>
    </div>
  </div>
);

/** The anchor block for whichever track is being shown. */
export const TrackAnchor = ({ alignment }) => {
  if (!alignment) return null;
  const kind = alignment.anchor_kind;
  return (
    <>
      {kind === "professional" ? <KplAnchor plan={alignment.anchor} /> : null}
      {kind === "grassroots" ? <GrassrootsAnchor anchor={alignment.anchor} /> : null}
      {kind === "both" ? (
        <>
          <KplAnchor plan={alignment.professional} />
          <GrassrootsAnchor anchor={alignment.grassroots} />
        </>
      ) : null}
      <AfconBand afcon={alignment.afcon} />
    </>
  );
};
